import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Customer-facing Account page: two chic side-by-side cards for Log In and
 * Sign Up. The Briology15 code is only issued when the newsletter checkbox
 * is ticked (or via the main newsletter popup). Sign Up currently records
 * the email + newsletter opt-in on the /api/waitlist endpoint; a full
 * password-based customer login can be layered in later.
 */
const Account = () => {
  const navigate = useNavigate();
  const [signupEmail, setSignupEmail] = useState("");
  const [signupName, setSignupName] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState(false);

  const submitSignup = async (e) => {
    e?.preventDefault?.();
    if (!signupEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/waitlist", {
        email: signupEmail.trim(),
        name: signupName.trim(),
        // Only trigger the code-bearing welcome email if the user opted in
        source: newsletterOptIn ? "newsletter-popup" : "account-signup",
      });
      setSignedUp(true);
      toast.success(
        newsletterOptIn
          ? "You're in — check your inbox for Briology15 💛"
          : "Account created ✨"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <section className="w-full" style={{ backgroundColor: "#FDE6EE" }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-3">
            Members
          </div>
          <h1 className="font-serif-display font-black text-5xl md:text-6xl leading-[0.95] tracking-tight">
            Your account.
          </h1>
        </div>
      </section>

      <section className="w-full bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-14 md:py-16 grid md:grid-cols-2 gap-6 md:gap-8">
          {/* LOG IN */}
          <div
            data-testid="account-login-card"
            className="border border-black p-6 md:p-8"
          >
            <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-2">
              Returning
            </div>
            <h2 className="font-serif-display font-black text-3xl mb-2">
              Log in.
            </h2>
            <p className="text-sm text-black/70 leading-[1.55] mb-5">
              Welcome back. Sign in to view your saved blends and order
              history.
            </p>
            <button
              data-testid="account-login-btn"
              onClick={() => navigate("/admin")}
              className="w-full bg-black text-white overline text-[11px] tracking-[0.22em] py-3 hover:bg-[#FF1493] transition-colors"
            >
              Log in
            </button>
            <p className="text-[11px] text-black/50 italic mt-3">
              Customer accounts coming soon. Admin login available for the
              team.
            </p>
          </div>

          {/* SIGN UP */}
          <div
            data-testid="account-signup-card"
            className="border border-black p-6 md:p-8"
            style={{ backgroundColor: "#FFF1F5" }}
          >
            <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-2">
              New here
            </div>
            <h2 className="font-serif-display font-black text-3xl mb-2">
              Sign up.
            </h2>
            <p
              data-testid="account-signup-perk"
              className="text-sm text-black/80 leading-[1.55] mb-5 italic"
            >
              Sign up for our newsletter and updates to receive{" "}
              <span className="font-semibold not-italic text-[#FF1493]">
                15% off your first order!
              </span>
            </p>

            {signedUp ? (
              <div
                data-testid="account-signup-success"
                className="border border-black bg-white p-4"
              >
                <div className="overline text-[10px] tracking-[0.3em] text-[#FF1493] mb-1">
                  {newsletterOptIn ? "Your code" : "Account ready"}
                </div>
                {newsletterOptIn ? (
                  <>
                    <div className="font-serif-display font-black text-2xl select-all">
                      Briology15
                    </div>
                    <p className="mt-2 text-xs text-black/60 italic">
                      Also sent to your inbox — use at checkout.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-black/70">
                    You&apos;re set. Head back to the builder to bottle your
                    first blend.
                  </p>
                )}
              </div>
            ) : (
              <form onSubmit={submitSignup} className="flex flex-col gap-3">
                <input
                  data-testid="account-signup-name"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="border border-black px-3 py-2.5 text-sm outline-none focus:bg-white bg-transparent"
                />
                <input
                  data-testid="account-signup-email"
                  type="email"
                  required
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  placeholder="you@briology.com"
                  className="border border-black px-3 py-2.5 text-sm outline-none focus:bg-white bg-transparent"
                />
                <label className="flex items-start gap-2 text-[12.5px] text-black/80 leading-snug cursor-pointer select-none">
                  <input
                    data-testid="account-signup-newsletter"
                    type="checkbox"
                    checked={newsletterOptIn}
                    onChange={(e) => setNewsletterOptIn(e.target.checked)}
                    className="mt-0.5 accent-[#FF1493]"
                  />
                  Sign up for newsletters and updates
                </label>
                <button
                  type="submit"
                  data-testid="account-signup-btn"
                  disabled={submitting}
                  className="mt-1 w-full bg-[#FF1493] text-white overline text-[11px] tracking-[0.22em] py-3 hover:bg-black disabled:opacity-60 transition-colors"
                >
                  {submitting ? "…" : "Create account"}
                </button>
                <p className="text-[10.5px] text-black/50 italic">
                  {newsletterOptIn
                    ? "You'll get one welcome email with your 15% off code."
                    : "Tick the box above to receive your 15% off code."}
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Account;
